import { Dropdown } from 'primereact/dropdown';
import { Tooltip } from 'primereact/tooltip';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { useGetCoachingBehaviorByIdQuery, useGetCoachingSkillQuery } from './skillBehaviorDropdownSlice';
import { CoachingBehavior, CoachingSkill, SkillBehaviorDropdownProps } from './types';
import { selectCurrentEmpid } from '../../pages/login/authSlice';

export const SkillBehaviorDropdown = (props: SkillBehaviorDropdownProps) => {
    const loggedEmpNum = useSelector(selectCurrentEmpid);
    const tooltipRefs = useRef<{ [key: string]: any }>({});
    const [optionsSkills, setOptionsSkills] = useState([]);
    const [optionsBehaviors, setOptionsBehaviors] = useState([]);

    const { data: skillData } = useGetCoachingSkillQuery({ employeeNumber: loggedEmpNum }, { skip: !loggedEmpNum });
    const { data: behaviorData } = useGetCoachingBehaviorByIdQuery(
        {
            employeeNumber: loggedEmpNum,
            coachingSkillId: props.skill,
        },
        {
            skip: !props.skill || !loggedEmpNum,
        }
    );

    useEffect(() => {
        if (skillData?.data.length > 0) {
            setOptionsSkills(skillData.data);
            if (props.skill > 0) {
                const selectedSkill: CoachingSkill | undefined = skillData.data.find(
                    (skill: CoachingSkill) => skill.coaching_skill_id === props.skill
                );
                if (selectedSkill && props?.setSkillName) {
                    props.setSkillName((selectedSkill as CoachingSkill)?.coaching_skill_name);
                }
            }
        }
    }, [props.skill, skillData]);
    useEffect(() => {
            if (skillData?.data?.length > 0) {
                if (props?.aiSkillName && props?.aiSkillName !== '') {
                    const foundOption = skillData?.data?.find((option: any) =>
                        option.coaching_skill_name.toLowerCase().trim() === props?.aiSkillName?.toLowerCase().trim()
                    );
                    if (foundOption && foundOption?.coaching_skill_id) {
                        props.setSkill(foundOption?.coaching_skill_id);
                        if( props?.setSkillName)
                            props.setSkillName(foundOption?.coaching_skill_name);
                    }
                }
            }
        }, [props?.aiSkillName]);
        useEffect(() => {
            if (behaviorData?.data?.length > 0) {
                if (props?.aiBehaviorName && props?.aiBehaviorName !== '') {
                    const foundOption = behaviorData?.data?.find((option: any) =>
                        option.coaching_behavior_name.toLowerCase().trim() === props?.aiBehaviorName?.toLowerCase().trim()
                    );
                    if (foundOption && foundOption?.coaching_behavior_id){
                        props.setBehavior(foundOption?.coaching_behavior_id);
                        if(props?.setBehaviorName)
                            props.setBehaviorName(foundOption?.coaching_behavior_name);
                    }
                }
            }
        }, [behaviorData, props?.aiBehaviorName]);
    useEffect(() => {
        if (behaviorData?.data.length > 0) {
            setOptionsBehaviors(behaviorData.data);
            if (props.behavior > 0) {
                const selectedBehavior: CoachingBehavior | undefined = behaviorData.data.find(
                    (behavior: CoachingBehavior) => behavior.coaching_behavior_id === props.behavior
                );
                if (selectedBehavior && props?.setBehaviorName) {
                    props.setBehaviorName((selectedBehavior as CoachingBehavior)?.coaching_behavior_name);
                }
            }
        }
    }, [props.behavior, behaviorData]);

    const customItemTemplate = (option: CoachingBehavior, tooltipId: string) => (
        <div className="d-flex align-items-center justify-content-between gap-4">
            <span>{option.coaching_behavior_name}</span>
            <div style={{ whiteSpace: 'pre-line', fontSize: '12px', wordWrap: 'break-word' }}>
                <React.Fragment key={option.coaching_behavior_id}>
                    <div
                        id={tooltipId}
                        className="triad-question-circle float-end"
                        onMouseEnter={(e) => tooltipRefs.current[tooltipId]?.show(e)}
                        onMouseLeave={(e) => tooltipRefs.current[tooltipId]?.hide(e)}
                    >
                        <i className="pi pi-question" style={{ color: 'white' }} />
                    </div>

                    <Tooltip
                        ref={(el) => {
                            tooltipRefs.current[tooltipId] = el;
                        }}
                        target={`#${tooltipId}`}
                        position="left"
                        className="p-tooltip-left"
                        style={{ maxWidth: '500px' }}
                    >
                        <div
                            dangerouslySetInnerHTML={{
                                __html: option.coaching_behavior_description,
                            }}
                        />
                    </Tooltip>
                </React.Fragment>
            </div>
        </div>
    );

    return (
        <div style={{ display: 'flex', gap: '100px' }}>
            <div className="col-md-4">
                <h6 id="coaching-skill-dropdown">Skill</h6>
                <Dropdown disabled={props.readOnly}
                    id="skill"
                    data-testid="coaching-skill"
                    aria-labelledby="coaching-skill-dropdown"
                    value={props.skill}
                    options={optionsSkills}
                    onChange={(event) => {
                        props.setSkill(event.target.value);
                        const selectedSkill: CoachingSkill | undefined = optionsSkills.find(
                            (skill: CoachingSkill) => skill.coaching_skill_id === event.target.value
                        );
                        if (selectedSkill && props?.setSkillName) {
                            props.setSkillName((selectedSkill as CoachingSkill)?.coaching_skill_name);
                        }
                    }}
                    optionValue={'coaching_skill_id'}
                    optionLabel={'coaching_skill_name'}
                    placeholder="Select Skill"
                    className={`custom-dropdown ${props?.fullWidth ? 'w-100' : ''}`}
                />
            </div>

            <div className="col-md-4">
                <h6 id="coaching-behavior-dropdown">Behavior</h6>
                <Dropdown disabled={props.readOnly}
                    id="behavior"
                    data-testid="coaching-behavior"
                    aria-labelledby="coaching-behavior-dropdown"
                    value={props.behavior}
                    options={optionsBehaviors}
                    onChange={(event) => {
                        props.setBehavior(event.target.value);
                        const selectedBehavior: CoachingBehavior | undefined = optionsBehaviors.find(
                            (behavior: CoachingBehavior) =>
                                behavior.coaching_behavior_id === event.target.value
                        );
                        if (selectedBehavior && props.setSimulationUrl) {
                            props.setSimulationUrl((selectedBehavior as CoachingBehavior)?.simulation_url);
                        }
                        if (selectedBehavior && props?.setBehaviorName) {
                            props.setBehaviorName(
                                (selectedBehavior as CoachingBehavior)?.coaching_behavior_name
                            );
                        }
                    }}
                    optionValue={'coaching_behavior_id'}
                    optionLabel={'coaching_behavior_name'}
                    placeholder="Select Behavior"
                    className={`custom-dropdown ${props?.fullWidth ? 'w-100' : ''}`}
                    itemTemplate={(option) =>
                        customItemTemplate(option, `tooltip-target-${option?.coaching_behavior_id}`)
                    }
                />
            </div>
        </div>
    );
};
